import React from 'react';
import { RecordButtonProps } from '../types';

// Format seconds to mm:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const MAX_RECORDING_TIME = 120; // 2 minutes
const SHOW_TIMER_THRESHOLD = 60; // Show timer after 1 minute

const RecordButton: React.FC<RecordButtonProps> = ({ isRecording, isProcessing, isValidating, recordingTime = 0, onStart, onStop, disabled }) => {
  const handleClick = (): void => {
    if (isRecording) {
      onStop();
    } else {
      onStart();
    }
  };

  const getButtonStyles = (): string => {
    if (isProcessing || isValidating) {
      return 'bg-gray-400 cursor-not-allowed';
    }
    if (isRecording) {
      return 'bg-red-500 hover:bg-red-600 animate-pulse';
    }
    return 'bg-blue-500 hover:bg-blue-600';
  };

  const getButtonText = (): string => {
    if (isValidating) {
      return 'Validating...';
    }
    if (isProcessing) {
      return 'Processing...';
    }
    if (isRecording) {
      return 'Stop Recording';
    }
    return 'Start Recording';
  };

  const isDisabled = disabled || isProcessing || isValidating;

  return (
    <div className="flex flex-col items-center gap-4">
      <button
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          px-8 py-4 rounded-full text-white font-semibold text-lg
          transition-all duration-200 transform
          ${getButtonStyles()}
          ${!isDisabled ? 'hover:scale-105 active:scale-95' : ''}
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center gap-3
        `}
      >
        {isRecording && (
          <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
        )}
        {(isProcessing || isValidating) && (
          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {getButtonText()}
      </button>

      {isRecording && (
        <div className="flex flex-col items-center gap-1">
          {recordingTime >= SHOW_TIMER_THRESHOLD ? (
            <>
              <p className="text-lg font-bold text-red-500">
                {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_TIME)}
              </p>
              <p className="text-xs text-red-400">
                {MAX_RECORDING_TIME - recordingTime <= 10
                  ? `Stopping in ${MAX_RECORDING_TIME - recordingTime}s...`
                  : 'Recording will stop at 2:00'}
              </p>
            </>
          ) : (
            <p className="text-sm text-red-500 font-medium">
              Recording in progress...
            </p>
          )}
        </div>
      )}
      {isValidating && (
        <p className="text-sm text-gray-500 font-medium">
          Checking your recording limit...
        </p>
      )}
    </div>
  );
};

export default RecordButton;
